<template>
  <div
    ref="item"
    class="vue-grid-layout"
    :style="mergedStyle"
  >
    <slot></slot>
    <grid-item
      ref="gridItem"
      class="vue-grid-placeholder"
      v-show="isDragging"
      :x="placeholder.x"
      :y="placeholder.y"
      :w="placeholder.w"
      :h="placeholder.h"
      :i="placeholder.i"
    ></grid-item>
  </div>
</template>
<script setup lang="ts">
import elementResizeDetectorMaker from 'element-resize-detector';
import mitt from 'mitt';
import {
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  ref,
  watch,
} from 'vue';
import { addWindowEventListener, removeWindowEventListener } from '../helpers/dom';
import {
  findOrGenerateResponsiveLayout,
  getBreakpointFromWidth,
  getColsFromBreakpoint,
} from '../helpers/responsive-utils';
import type { Layout } from '../helpers/utils';
import {
  bottom,
  cloneLayout,
  compact,
  getAllCollisions,
  getLayoutItem,
  moveElement,
  validateLayout,
} from '../helpers/utils';
import GridItem from './grid-item.vue';

defineOptions({
  name: 'GridLayout',
});

const props = withDefaults(
  defineProps<{
    // If true, the container height swells and contracts to fit contents
    autoSize?: boolean;
    colNum?: number;
    rowHeight?: number;
    maxRows?: number;
    margin?: number[];
    isDraggable?: boolean;
    isResizable?: boolean;
    isMirrored?: boolean;
    useCssTransforms?: boolean;
    verticalCompact?: boolean;
    layout: Layout;
    responsive?: boolean;
    responsiveLayouts?: Record<string, any>;
    breakpoints?: Record<string, number>;
    cols?: Record<string, number>;
    preventCollision?: boolean;
    usePercentages?: boolean;
  }>(),
  {
    autoSize: true,
    colNum: 12,
    rowHeight: 10,
    maxRows: Infinity,
    margin: () => [10, 10],
    isDraggable: false,
    isResizable: false,
    isMirrored: false,
    useCssTransforms: true,
    verticalCompact: true,
    responsive: false,
    responsiveLayouts: () => ({}),
    breakpoints: () => ({ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }),
    cols: () => ({ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }),
    preventCollision: false,
    usePercentages: false,
  },
);

const emit = defineEmits<{
  (e: 'layout-created', layout: Layout): void;
  (e: 'layout-mounted', layout: Layout): void;
  (e: 'layout-before-mount', layout: Layout): void;
  (e: 'layout-updated', layout: Layout): void;
  (e: 'layout-ready', layout: Layout): void;
  (e: 'update:layout', layout: Layout): void;
  (e: 'breakpoint-changed', breakpoint: string | null, layout: Layout): void;
}>();

const eventBus = mitt();
provide('eventBus', eventBus);
provide('usePercentages', props.usePercentages);
provide('layout', props.layout);

const item = ref<HTMLElement | null>(null);
const gridItem = ref(null);
const width = ref<number | null>(null);
const mergedStyle = ref<Record<string, any>>({});
const lastLayoutLength = ref(0);
const isDragging = ref(false);
const placeholder = reactive({
  x: 0 as string | number,
  y: 0 as string | number,
  w: 0 as string | number,
  h: 0 as string | number,
  i: -1 as string | number,
});
const layouts = ref<Record<string, Layout>>({}); // array to store all layouts from different breakpoints
const lastBreakpoint = ref<string | null>(null); // store last active breakpoint
const originalLayout = ref<Layout | null>(null); // store original Layout
const erd = ref<any>(null);

const getLayoutEl = () => {
  return item.value;
};

const updateHeight = () => {
  mergedStyle.value = {
    height: containerHeight(),
  };
  if (props.usePercentages) {
    mergedStyle.value.width = '100%';
  }
};

const onWindowResize = () => {
  if (item.value !== null && item.value !== undefined) {
    width.value = item.value.offsetWidth;
  }
  eventBus.emit('resizeEvent', {});
};

const containerHeight = () => {
  if (props.usePercentages) return '100%';
  if (!props.autoSize) return;
  return bottom(props.layout) * (props.rowHeight + props.margin[1]) + props.margin[1] + 'px';
};

const dragEvent = (
  eventName: string,
  id: string | number,
  x: number,
  y: number,
  h: number,
  w: number,
) => {
  let l = getLayoutItem(props.layout, String(id));
  if (l === undefined || l === null) {
    l = { x: 0, y: 0, w: 0, h: 0, i: String(id) };
  }
  if (eventName === 'dragmove' || eventName === 'dragstart') {
    placeholder.i = id;
    placeholder.x = l.x;
    placeholder.y = l.y;
    placeholder.w = w;
    placeholder.h = h;
    nextTick(() => {
      isDragging.value = true;
    });
    eventBus.emit('updateWidth', width.value);
  } else {
    nextTick(() => {
      isDragging.value = false;
    });
  }
  // Move the element to the dragged location.
  moveElement(props.layout, l, x, y, true, props.preventCollision);
  compact(props.layout, props.verticalCompact);
  // needed because vue can't detect changes on array element properties
  eventBus.emit('compact');
  updateHeight();
  if (eventName === 'dragend') emit('layout-updated', props.layout);
};

const responsiveGridLayout = () => {
  if (!width.value) return;
  const newBreakpoint = getBreakpointFromWidth(props.breakpoints, width.value);
  const newCols = getColsFromBreakpoint(newBreakpoint, props.cols);
  // save actual layout in layouts
  if (lastBreakpoint.value != null && !layouts.value[lastBreakpoint.value])
    layouts.value[lastBreakpoint.value] = cloneLayout(props.layout);
  // Find or generate a new layout.
  const layout = findOrGenerateResponsiveLayout(
    originalLayout.value!,
    layouts.value,
    props.breakpoints as any,
    newBreakpoint,
    lastBreakpoint.value as any,
    newCols,
    props.verticalCompact,
  );
  // Store the new layout.
  layouts.value[newBreakpoint] = layout;
  if (lastBreakpoint.value !== newBreakpoint) {
    emit('breakpoint-changed', newBreakpoint, layout);
  }
  // new prop sync
  emit('update:layout', layout);
  lastBreakpoint.value = newBreakpoint;
  eventBus.emit('setColNum', getColsFromBreakpoint(newBreakpoint, props.cols));
};

const resizeEvent = (
  eventName: string,
  id: string | number,
  x: number,
  y: number,
  h: number,
  w: number,
) => {
  let l = getLayoutItem(props.layout, String(id));
  if (l === undefined || l === null) {
    l = { h: 0, w: 0, x: 0, y: 0, i: String(id) };
  }
  let hasCollisions = false;
  if (props.preventCollision) {
    const collisions = getAllCollisions(props.layout, { ...l, w, h }).filter(
      layoutItem => layoutItem.i !== l!.i,
    );
    hasCollisions = collisions.length > 0;
    // If we're colliding, we need adjust the placeholder.
    if (hasCollisions) {
      // adjust w && h to maximum allowed space
      let leastX = Infinity;
      let leastY = Infinity;
      collisions.forEach(layoutItem => {
        if (layoutItem.x > l!.x) leastX = Math.min(leastX, layoutItem.x);
        if (layoutItem.y > l!.y) leastY = Math.min(leastY, layoutItem.y);
      });
      if (Number.isFinite(leastX)) l.w = leastX - l.x;
      if (Number.isFinite(leastY)) l.h = leastY - l.y;
    }
  }
  if (!hasCollisions) {
    // Set new width and height.
    l.w = w;
    l.h = h;
  }
  if (eventName === 'resizestart' || eventName === 'resizemove') {
    placeholder.i = id;
    placeholder.x = x;
    placeholder.y = y;
    placeholder.w = l.w;
    placeholder.h = l.h;
    nextTick(() => {
      isDragging.value = true;
    });
    eventBus.emit('updateWidth', width.value);
  } else {
    nextTick(() => {
      isDragging.value = false;
    });
  }
  if (props.responsive) responsiveGridLayout();
  compact(props.layout, props.verticalCompact);
  eventBus.emit('compact');
  updateHeight();
  if (eventName === 'resizeend') emit('layout-updated', props.layout);
};

const initResponsiveFeatures = () => {
  layouts.value = Object.assign({}, props.responsiveLayouts);
};

const findDifference = (layout: Layout, originalLayout: Layout) => {
  const uniqueResultOne = layout.filter(obj => {
    return !originalLayout.some(obj2 => {
      return obj.i === obj2.i;
    });
  });
  const uniqueResultTwo = originalLayout.filter(obj => {
    return !layout.some(obj2 => {
      return obj.i === obj2.i;
    });
  });
  return uniqueResultOne.concat(uniqueResultTwo);
};

const layoutUpdate = () => {
  if (props.layout !== undefined && originalLayout.value !== null) {
    if (props.layout.length !== originalLayout.value.length) {
      const diff = findDifference(props.layout, originalLayout.value);
      if (diff.length > 0) {
        if (props.layout.length > originalLayout.value.length) {
          originalLayout.value = originalLayout.value.concat(diff);
        } else {
          originalLayout.value = originalLayout.value.filter(obj => {
            return !diff.some(obj2 => {
              return obj.i === obj2.i;
            });
          });
        }
      }
      lastLayoutLength.value = props.layout.length;
      initResponsiveFeatures();
    }
    if (props.usePercentages) {
      // Skip correctBounds on layout update to keep original values
    }
    compact(props.layout, props.verticalCompact);
    eventBus.emit('updateWidth', width.value);
    updateHeight();
    emit('layout-updated', props.layout);
  }
};

const resizeEventHandler = ({ eventType, i, x, y, h, w }: any) => {
  resizeEvent(eventType, i, x, y, h, w);
};
const dragEventHandler = ({ eventType, i, x, y, h, w }: any) => {
  dragEvent(eventType, i, x, y, h, w);
};

onBeforeMount(() => {
  emit('layout-before-mount', props.layout);
});

onMounted(() => {
  eventBus.on('resizeEvent', resizeEventHandler);
  eventBus.on('dragEvent', dragEventHandler);
  emit('layout-created', props.layout);

  emit('layout-mounted', props.layout);
  nextTick(() => {
    validateLayout(props.layout);
    originalLayout.value = props.layout;
    if (props.usePercentages) {
      // Skip correctBounds on initialization to keep original values
    }
    nextTick(() => {
      onWindowResize();
      initResponsiveFeatures();
      addWindowEventListener('resize', onWindowResize);
      compact(props.layout, props.verticalCompact);
      emit('layout-updated', props.layout);
      updateHeight();
      nextTick(() => {
        erd.value = elementResizeDetectorMaker({
          strategy: 'scroll',
          callOnAdd: false,
        });
        erd.value.listenTo(item.value, () => {
          onWindowResize();
        });
      });
    });
  });
});

onBeforeUnmount(() => {
  eventBus.off('resizeEvent', resizeEventHandler);
  eventBus.off('dragEvent', dragEventHandler);
  removeWindowEventListener('resize', onWindowResize);
  if (erd.value && item.value) {
    erd.value.uninstall(item.value);
  }
});

watch(width, (_newval, oldval) => {
  nextTick(() => {
    eventBus.emit('updateWidth', width.value);
    if (oldval === null) {
      nextTick(() => {
        emit('layout-ready', props.layout);
      });
    }
    updateHeight();
  });
});

watch(
  () => props.layout.length,
  () => {
    layoutUpdate();
  },
);

watch(
  () => props.layout,
  () => {
    layoutUpdate();
  },
);

watch(
  () => props.colNum,
  val => {
    eventBus.emit('setColNum', val);
  },
);

watch(
  () => props.rowHeight,
  () => {
    eventBus.emit('setRowHeight', props.rowHeight);
  },
);

watch(
  () => props.isDraggable,
  () => {
    eventBus.emit('setDraggable', props.isDraggable);
  },
);

watch(
  () => props.isResizable,
  () => {
    eventBus.emit('setResizable', props.isResizable);
  },
);

watch(
  () => props.responsive,
  () => {
    if (!props.responsive) {
      emit('update:layout', originalLayout.value!);
      eventBus.emit('setColNum', props.colNum);
    }
    onWindowResize();
  },
);

watch(
  () => props.maxRows,
  () => {
    eventBus.emit('setMaxRows', props.maxRows);
  },
);

watch(
  () => props.margin,
  () => {
    updateHeight();
  },
);

defineExpose({
  getLayoutEl,
});
</script>
<style>
.vue-grid-layout {
  position: relative;
}
</style>
